import { Fragment, useCallback, useMemo, useState } from 'react';

import { useResponsiveDesign } from '@zola/zola-ui/src/contexts/ResponsiveDesignContext/ResponsiveDesignContext';

import cx from 'classnames';
import Sticky from 'react-stickynode';

import { useSearchResultsPageContext } from '~/pages/couples/explore/components/SearchResults/contexts/SearchResultsPageContext';
import { AllFacetParentKey } from '~/types/types';

import { PageSection } from '../../../PageSection/PageSection';
import SaveNewSearchButton from '../../../SavedSearches/SaveNewSearchButton';
import { useStickyScrollContext } from '../../contexts/StickyScrollContext';
import { useVendorSearchNavigation } from '../../contexts/VendorSearchNavigationContext';
import FacetButton, { isSortingFilter } from './facets/FacetButton/FacetButton';
import { FacetSideDrawer } from './facets/FacetSideDrawer/FacetSideDrawer';
import { SearchFacets } from './facets/SearchFacets/SearchFacets';
import { SearchTags } from './facets/SearchTags/SearchTags';
import { useSearchCategories } from './useSearchCategories';

import styles from './searchControls.module.less';

export const SearchControls = (): JSX.Element => {
  const { isDesktop } = useResponsiveDesign();

  const [drawerOpenTo, setDrawerOpenTo] = useState<null | true | AllFacetParentKey>(null);

  const { direction, innerZ, top, isGuest, isStickyActive, setIsStickyActive } =
    useStickyScrollContext();

  const { vendorSearch, selectedFacetOptions } = useSearchResultsPageContext();
  const { navigateToSrp } = useVendorSearchNavigation();

  const { selectedTaxonomy, availabilityDates } = vendorSearch;

  /** These are the available categories + facets used in the search controls from /inspire/search */
  const allFacetCategories = useSearchCategories(vendorSearch);
  const sortingFilter = useMemo(
    () => allFacetCategories.find(isSortingFilter),
    [allFacetCategories]
  );

  const handleStickyStateChange = useCallback(
    (status: Sticky.Status) => {
      setIsStickyActive(status.status === Sticky.STATUS_FIXED);
    },
    [setIsStickyActive]
  );

  const clearFilters = useCallback(() => {
    navigateToSrp({
      awardGroups: null,
      selectedFacets: null,
      price: null,
      capacity: null,
      searchTerm: null,
      availabilityDates: null,
      metroTypes: null,
      catererTypes: null,
      page: undefined,
    });
  }, [navigateToSrp]);

  return (
    <Fragment>
      <div className={styles.header}>
        {/* -------------------
         * Filter controls
         * ----------------- */}
        {selectedTaxonomy && (
          <Sticky
            className={cx(styles.sticky)}
            innerClass={cx(styles.stickyWrapper, {
              [styles.postAuth]: !isGuest,
              [styles.preAuth]: isGuest,
            })}
            innerActiveClass={cx(styles.stickyActive, 'active')}
            innerZ={direction === 'down' ? innerZ : 'initial'}
            onStateChange={handleStickyStateChange}
            enabled={direction === 'down'}
            top={top}
          >
            <div className={styles.stickyContainer}>
              <div className={styles.searchFacetsWrapper}>
                <SearchFacets
                  // This forces a re-render if the categories change
                  key={allFacetCategories.map(({ key }) => key).join('-')}
                  categories={allFacetCategories}
                  setDrawerOpenTo={setDrawerOpenTo}
                  isStickyActive={isStickyActive}
                />
              </div>
              {sortingFilter && !isStickyActive && (
                <FacetButton
                  category={sortingFilter}
                  openSideDrawer={() => setDrawerOpenTo('sorting')}
                  className={styles.filterButtonRightRail}
                />
              )}
            </div>
          </Sticky>
        )}

        {/* -------------------
         * Chips
         * ----------------- */}
        {(selectedFacetOptions.length !== 0 || availabilityDates?.length) && (
          <PageSection noPadding contentClassName={styles.searchTags}>
            <SearchTags clearFilters={clearFilters} />
          </PageSection>
        )}
      </div>

      {/* Below here, components are fixed in their position */}
      {Boolean(drawerOpenTo) && (
        <FacetSideDrawer
          drawerOpenTo={drawerOpenTo}
          onClose={() => setDrawerOpenTo(null)}
          categories={allFacetCategories}
          clearFilters={clearFilters}
        />
      )}

      {!drawerOpenTo && selectedTaxonomy && !isDesktop && (
        <div className={styles.mobileSaveSearchWrapper}>
          <SaveNewSearchButton
            vendorSearch={vendorSearch}
            selectedFacetOptions={selectedFacetOptions}
            availabilityDates={availabilityDates}
            size="large"
            cta="Save this search"
            component="button"
          />
        </div>
      )}
    </Fragment>
  );
};
